import { Button, Icon, Modal, addToast } from '@octano/global-ui';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationsKeys } from '../../../locales/translations';

export type RegistrationModalErrorMethods = {
  open: (message?: null | string | string[]) => void;
  close: () => void;
};

type RegistrationModalErrorProps = {
  hasPassport: boolean;
  loading?: boolean;
  onBackToLogin?: () => void;
  onLogin?: () => void;
  onGoToWaitlist?: () => void;
};

enum ErrorCodes {
  UserAlreadyExist = 'USER_ALREADY_EXIST',
  NonActivePostulation = 'NON_ACTIVE_POSTULATION',
  MinimunAgeFailed = 'MINIMUN_AGE_FAILED',
  QuotaExceeded = 'QUOTA_EXCEEDED',
}

function isValidErrorCode(error: string): boolean {
  return Object.values(ErrorCodes).some((baseErrorCode) =>
    new RegExp(`^${baseErrorCode}(_\\d+)?$`).test(error),
  );
}

const RegistrationModalError = (
  {
    loading,
    hasPassport,
    onBackToLogin,
    onLogin,
    onGoToWaitlist,
  }: RegistrationModalErrorProps,
  ref: React.Ref<RegistrationModalErrorMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.AUTH_REGISTER);

  const [open, setOpen] = useState<boolean>(false);
  const [code, setCode] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [button, setButton] = useState<string>('');
  const [closeButton, setCloseButton] = useState<string>('');

  const handleClose = useCallback(() => {
    setCode('');
    setTitle('');
    setBody('');
    setButton('');
    setCloseButton('');
    setOpen(false);
  }, []);

  const handleOpen = useCallback(
    (message?: null | string | string[]) => {
      let error = Array.isArray(message) ? message[0] : message;
      let minimunAge = 17;

      if (error?.includes(ErrorCodes.MinimunAgeFailed)) {
        minimunAge = parseInt(error.replace(/^\D+/g, ''));
        error = error.replace(/_\d+$/, '');
      }

      if (error && isValidErrorCode(error)) {
        const values = {
          minimunAge,
          accessType: hasPassport ? t(`form.passportNumber`) : t(`form.rut`),
        };

        setCode(error);
        setTitle(t(`errorsModals.${error}.title`, values));
        setBody(t(`errorsModals.${error}.body`, values));
        setButton(t(`errorsModals.${error}.button`, values));
        if (error === 'QUOTA_EXCEEDED' || error === 'USER_ALREADY_EXIST') {
          setCloseButton(t(`errorsModals.${error}.close`, values));
        }
        setOpen(true);
        return;
      }

      return addToast({
        icon: 'error',
        color: 'danger',
        text: t(`errors.DEFAULT`),
      });
    },
    [hasPassport, t],
  );

  const handleContinue = useCallback(() => {
    if (loading) {
      return;
    }
    handleClose();
    if (code === 'NON_ACTIVE_POSTULATION') {
      !!onBackToLogin && onBackToLogin();
    }
    if (code === 'USER_ALREADY_EXIST') {
      !!onLogin && onLogin();
    }
    if (code === 'QUOTA_EXCEEDED') {
      !!onGoToWaitlist && onGoToWaitlist();
    }
  }, [loading, code, handleClose, onBackToLogin, onLogin, onGoToWaitlist]);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  return (
    <Modal isOpen={open} toggle={handleClose} closeOnBackdrop={false}>
      <div className="text-center">
        <Icon className="mb-2" name="warning" size="52px" color="secondary" />
        <p className="pt-4 text-dark fw-400 fs-22">{title}</p>
        <p className="fs-18 fw-300">{body}</p>
        <Button
          type="button"
          className="mt-4"
          loading={loading}
          text={button}
          onClick={handleContinue}
          fullwidth
        />
        {!!closeButton?.trim() && (
          <Button
            type="button"
            className="mt-42"
            loading={loading}
            outlined
            text={closeButton}
            onClick={handleClose}
            fullwidth
          />
        )}
      </div>
    </Modal>
  );
};

export default forwardRef(RegistrationModalError);
